import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import DialogComponent from '../dialog/dialog.component';
import DialogPayment from '../dialog-payment';
import { LocalStorageService } from '../../configuration';
import {
  getMembershipStatus,
  getPublicMembership,
  joinMembership,
  joinMembershipWithCabPlus,
  MembershipStatus,
  PublicMembership,
} from '../../api/payment/membership.api';
import { formatMemberUntil, joinButtonLabel, pickJoinAction } from '../../utils/membership';
import { routePaths } from '../../routes/routes-path';

interface Props {
  creatorId: string;
  creatorName: string;
  visible: boolean;
  onClose: () => void;
  onJoined: (s: MembershipStatus) => void;
  /** Báo mỗi lần hộp thoại có trạng thái mới (tải, tham gia xong, tải lại sau lỗi) để nơi mở nó cập nhật theo. */
  onStatus?: (s: MembershipStatus) => void;
}

const btnCls =
  'inline-flex w-full items-center justify-center rounded-lg bg-[#0057FF] px-4 h-11 text-base font-semibold text-white hover:bg-[#0049d6] disabled:opacity-60 disabled:cursor-not-allowed';

const JoinMembershipDialog: FC<Props> = ({
  creatorId,
  creatorName,
  visible,
  onClose,
  onJoined,
  onStatus,
}) => {
  const isLogged = !!LocalStorageService.getItem('accessToken');
  const [pub, setPub] = useState<PublicMembership | null>(null);
  const [status, setStatus] = useState<MembershipStatus | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const lock = useRef(false);
  const onStatusRef = useRef(onStatus);
  onStatusRef.current = onStatus;

  const creatorRef = useRef(creatorId);
  creatorRef.current = creatorId;

  const applyStatus = useCallback((s: MembershipStatus) => {
    setStatus(s);
    onStatusRef.current?.(s);
  }, []);

  const refreshStatus = useCallback(() => {
    if (!isLogged) return;
    const forCreator = creatorId;
    getMembershipStatus(forCreator)
      .then((s) => {
        // Đã đổi sang streamer khác trong lúc chờ thì bỏ kết quả cũ.
        if (creatorRef.current === forCreator) applyStatus(s);
      })
      .catch(() => undefined);
  }, [isLogged, creatorId, applyStatus]);

  // Đổi streamer: bỏ trạng thái của streamer trước để không hiện hay gửi nhầm.
  useEffect(() => {
    setPub(null);
    setStatus(null);
  }, [creatorId]);

  useEffect(() => {
    if (!visible) return;
    const forCreator = creatorId;
    getPublicMembership(forCreator)
      .then((p) => {
        if (creatorRef.current === forCreator) setPub(p);
      })
      .catch(() => undefined);
    refreshStatus();
  }, [visible, creatorId, refreshStatus]);

  const action = pickJoinAction(status, isLogged);
  const memberCount = status?.memberCount ?? pub?.memberCount ?? 0;
  const perks = (pub?.perks ?? []).slice(0, 4);

  const submit = async () => {
    if (!status || lock.current) return;
    lock.current = true;
    setSubmitting(true);
    try {
      const next =
        action === 'free'
          ? await joinMembershipWithCabPlus(creatorId)
          : await joinMembership(creatorId, status.payPriceCa);
      applyStatus(next);
      toast.success(`Bạn đã là hội viên kênh ${creatorName}.`);
      onJoined(next);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Chưa tham gia được, bạn thử lại sau nhé.');
      // Có thể tiền đã trừ dù yêu cầu báo lỗi: tải lại để hiện đúng trạng thái, tránh bấm trả lần nữa.
      refreshStatus();
    } finally {
      lock.current = false;
      setSubmitting(false);
    }
  };

  const discounted = !!status && status.payPriceCa < status.priceCa;
  const showUsedSlot =
    !!status && !status.slot.available && !!status.slot.usedForCreatorName;

  return (
    <DialogComponent
      visible={visible}
      title={`Hội viên kênh ${creatorName}`}
      size="md"
      onClose={onClose}
    >
      <div className="flex flex-col gap-3 p-6 text-sm text-[#18191E] dark:text-zinc-50">
        <p>
          Có Cab+, mỗi tháng bạn được tặng 1 lượt hội viên miễn phí cho streamer bạn thích.
        </p>
        {perks.length > 0 && (
          <ul className="list-disc pl-5 text-[#657384] dark:text-slate-300">
            {perks.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}
        <p className="font-semibold">{memberCount.toLocaleString('vi-VN')} hội viên</p>
        {status?.isMember && status.expiresAt && (
          <p className="font-medium text-[#F59E0B]">
            Bạn là hội viên đến {formatMemberUntil(status.expiresAt)}.
          </p>
        )}
        {discounted && action !== 'free' && (
          <p className="text-[#657384] dark:text-slate-300">
            <s>{status!.priceCa.toLocaleString('vi-VN')} Ca</s>
          </p>
        )}
        {action === 'login' && (
          <Link to={routePaths.login} className={btnCls}>
            Đăng nhập để tham gia
          </Link>
        )}
        {(action === 'free' || action === 'pay') && status && (
          <button type="button" className={btnCls} disabled={submitting} onClick={submit}>
            {submitting ? 'Đang gửi…' : joinButtonLabel(action, status)}
          </button>
        )}
        {action === 'topup' && (
          <>
            <p className="text-[#657384] dark:text-slate-300">
              Số dư chưa đủ để tham gia, bạn nạp thêm Ca nhé.
            </p>
            <DialogPayment onConfirm={() => undefined} onCancel={() => undefined} />
          </>
        )}
        {status && !status.isPremium && (
          <p className="text-xs text-[#657384] dark:text-slate-400">
            <Link to={routePaths.cabPlus} className="text-[#0057FF] hover:underline">
              Có Cab+ được giảm {status.discountPercent}%
            </Link>
          </p>
        )}
        {showUsedSlot && (
          <p className="text-xs text-[#657384] dark:text-slate-400">
            Tháng này bạn đã tặng lượt cho {status!.slot.usedForCreatorName}, tháng sau mới có
            lượt mới.
          </p>
        )}
      </div>
    </DialogComponent>
  );
};

export default JoinMembershipDialog;
